import { useLingui } from '@lingui/react/macro';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useEffect } from 'react';

import { appendNewUserFlag } from '#modules/user/features/onboarding/utils/appendNewUserFlag';
import { isNewUserFlag } from '#modules/user/features/onboarding/utils/isNewUserFlag';
import { getMeOptions } from '#modules/user/model/me';
import { toaster } from '#features/ui/components/Toaster';
import { envConfig } from '#constants/envConfig';
import { setNativeToken } from './nativeAuth';
import { NATIVE_OAUTH_DEEPLINK_PREFIX } from './nativeOAuth';
import { exchangeNativeLoginCode } from './nativeOAuthExchange';
import { markLaunchUrlHandled, wasLaunchUrlHandled } from './nativeOAuthLaunch';
import { markNativeLinkAnswered, takeUnansweredNativeLink } from './nativeOAuthLinkWatch';
import { clearNativeOAuthPending, isNativeOAuthPending, takePkceVerifier } from './nativeOAuthPkce';
import { takeNativeOAuthReturn } from './nativeOAuthReturn';

/**
 * Listens for the OAuth deep-link callback in the native shell. When
 * teamplay-user finishes the system-browser OAuth flow it redirects to
 * `gg.teamplay.twa://oauth-callback?token=…` (or `?error=…`); Android hands that
 * URL to the app here. We store the JWT, close the browser tab, refresh auth and
 * route to the dashboard. No-op / tree-shaken on web.
 *
 * Mount once, high in the tree (inside the router + query providers).
 */
export const useNativeOAuthDeepLink = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const router = useRouter();
  const { t } = useLingui();

  useEffect(() => {
    if (!envConfig.IS_NATIVE) {
      return;
    }

    let remove: (() => void) | undefined;
    let active = true;

    (async () => {
      const [{ App }, { Browser }] = await Promise.all([
        import('@capacitor/app'),
        import('@capacitor/browser'),
      ]);

      const handleDeepLink = async (url: string | null | undefined, fromLaunch = false) => {
        if (!url || !url.startsWith(NATIVE_OAUTH_DEEPLINK_PREFIX)) {
          return;
        }

        let token: string | null = null;
        let code: string | null = null;
        let error: string | null = null;
        let newUser: string | null = null;
        try {
          const parsed = new URL(url);
          token = parsed.searchParams.get('token');
          code = parsed.searchParams.get('code');
          error = parsed.searchParams.get('error');
          newUser = parsed.searchParams.get('newUser');
        } catch {
          // malformed deep link — ignore
        }

        // Повтор уже обработанного адреса - это не новый ответ. На iOS `getLaunchUrl` отдаёт не
        // ссылку запуска, а последнюю открытую, и после перезагрузки страницы (смена языка, переход
        // из колокольчика, сторож гидрации) прежний возврат пришёл бы снова: ложная ошибка
        // привязки, лишнее «Account linked», потерянный секрет у нового входа. Поэтому из
        // `getLaunchUrl` пропускаем любой уже виденный адрес, а живое событие – только с кодом:
        // он одноразовый и в адресе уникален, тогда как токен или та же ошибка у новой попытки
        // законно совпадают. Отметку ставим сразу, до первого await: на холодном старте iOS
        // доставляет возврат и событием, и через `getLaunchUrl`.
        if ((code || fromLaunch) && wasLaunchUrlHandled(url)) {
          return;
        }
        markLaunchUrlHandled(url);

        // Токен в адресе принимаем только после входа, который начало это приложение. Иначе любое
        // другое приложение могло бы прислать свой токен и молча завести человека в чужой аккаунт.
        if (token && !code && !isNativeOAuthPending()) {
          return;
        }

        // Провайдер ответил - неважно, удачей или отказом. Закрытие окна следом не должно
        // приниматься за молчание (см. `nativeOAuthLinkWatch`).
        markNativeLinkAnswered();

        try {
          await Browser.close();
        } catch {
          // tab may already be closed
        }

        // Привязка, а не вход: приложение уже авторизовано, токен сессии в возврате не приходит
        // и не должен — он осел бы в истории системного браузера. Приходит имя провайдера либо
        // причина отказа, и причины разделены намеренно: человек мог десять минут восстанавливать
        // пароль у провайдера, и «истёк срок» обязано отличаться от «что-то пошло не так».
        const linked = new URL(url).searchParams.get('linked');
        if (linked) {
          await queryClient.invalidateQueries({ queryKey: getMeOptions().queryKey });
          await queryClient.invalidateQueries({ queryKey: ['apple-oauth-account'] });
          toaster.create({
            title: t`Account linked`,
            type: 'success',
            duration: 5000,
            id: 'oauth-link-result',
          });
          return;
        }

        if (error === 'link.expired' || error === 'link.invalid') {
          toaster.create({
            title:
              error === 'link.expired'
                ? t`The linking window expired. Please try again.`
                : t`This linking link is no longer valid. Please try again.`,
            type: 'error',
            duration: 7000,
            id: 'oauth-link-result',
          });
          return;
        }

        // Новая схема: в адресе возврата только одноразовый код, токен забираем по HTTPS с
        // секретом, который приложение создало до открытия браузера. Токен прямо в адресе
        // остался для старых сборок сервера и для WebView без SHA-256 (см. nativeOAuthPkce).
        if (!token && code) {
          const verifier = takePkceVerifier();
          try {
            const exchanged = verifier ? await exchangeNativeLoginCode(code, verifier) : null;
            if (exchanged) {
              token = exchanged.token;
              newUser = exchanged.newUser ? 'true' : null;
            }
          } catch {
            // Сеть или отказ сервера: ниже человек увидит одну и ту же ошибку.
          }
          if (!token) {
            toaster.create({
              title: t`Could not sign in. Please try again.`,
              type: 'error',
              duration: 5000,
              id: 'oauth-error-message',
            });
            return;
          }
        }

        if (token) {
          clearNativeOAuthPending();
          await setNativeToken(token);
          await queryClient.invalidateQueries({ queryKey: getMeOptions().queryKey });
          await queryClient.invalidateQueries({ queryKey: ['apple-oauth-account'] });
          router.invalidate();

          // Человек уходил в браузер с конкретной страницы — на неё и возвращаем. Главный случай
          // — приглашение по ссылке: `?invite=<код>` живёт только в адресе, и без возврата место
          // в сборе занять уже нечем, хотя лендинг обещал «подтвердите сразу после входа».
          //
          // Новичок здесь ничем не отличается: он тоже пришёл занять место, а не заполнять
          // профиль. Раньше его уводили на форму профиля, и приглашение терялось — «ссылка ждёт
          // в чате» на деле означало, что сбор начинался без него. Метку новичка везём с собой:
          // попап онбординга висит в layout, всплывает на любой странице, а на самом приглашении
          // откладывает себя до следующего перехода.
          const returnPath = takeNativeOAuthReturn();

          if (returnPath) {
            navigate(
              isNewUserFlag(newUser) ? { href: appendNewUserFlag(returnPath) } : { to: returnPath }
            );
          } else if (isNewUserFlag(newUser)) {
            navigate({
              to: '/{-$locale}/dashboard/settings/profile',
              search: { newUser: 'true' },
            });
          } else {
            navigate({ to: '/{-$locale}/dashboard' });
          }
        } else if (error) {
          toaster.create({
            title: error,
            type: 'error',
            duration: 5000,
            id: 'oauth-error-message',
          });
        }
      };

      const handle = await App.addListener('appUrlOpen', ({ url }) => handleDeepLink(url));

      // Холодный старт: Android мог выгрузить приложение, пока человек был в браузере. Возврат
      // приходит в новый процесс до того, как мы подписались, и событие `appUrlOpen` пропадает –
      // человек вернулся бы разлогиненным. Запускающую ссылку забираем отдельно, один раз на
      // запуск: Capacitor отдаёт её и после перезагрузки страницы.
      try {
        const launch = await App.getLaunchUrl();
        if (active && launch?.url?.startsWith(NATIVE_OAUTH_DEEPLINK_PREFIX)) {
          void handleDeepLink(launch.url, true);
        }
      } catch {
        // Запуска по ссылке не было – обычное нажатие на иконку.
      }

      /*
        Окно привязки закрылось, а ответа не было.

        Так выглядят обе беды разом: страница провайдера не загрузилась (видели полностью чёрное
        окно) и человек сам закрыл её на полпути. Раньше он просто возвращался в приложение, не
        находил привязки и не получал ни слова. Теперь перечитываем привязки - список обязан
        показать настоящий итог - и говорим, что попытка не завершилась.
      */
      const finished = await Browser.addListener('browserFinished', () => {
        if (!takeUnansweredNativeLink()) {
          return;
        }

        void queryClient.invalidateQueries({ queryKey: getMeOptions().queryKey });
        void queryClient.invalidateQueries({ queryKey: ['apple-oauth-account'] });
        toaster.create({
          title: t({
            id: 'oauth:link.notCompleted',
            message: 'Linking was not completed. Please try again.',
          }),
          type: 'error',
          duration: 7000,
          id: 'oauth-link-result',
        });
      });

      if (active) {
        remove = () => {
          handle.remove();
          finished.remove();
        };
      } else {
        handle.remove();
        finished.remove();
      }
    })();

    return () => {
      active = false;
      remove?.();
    };
  }, [queryClient, navigate, router, t]);
};
